import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Gift, Copy, Users, TrendingUp, Share2, Sparkles, CheckCircle2 } from 'lucide-react';
import { referralAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

export default function ReferralPage() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try { const res = await referralAPI.getInfo(); setReferral(res.data); } catch (err) { console.error('Referral load error:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const referralLink = `${window.location.origin}/register?ref=${referral?.referral_code || user?.referral_code || ''}`;
  const copyCode = () => { navigator.clipboard.writeText(referral?.referral_code || user?.referral_code || ''); toast.success(t('referral.codeCopied')); };
  const copyLink = () => { navigator.clipboard.writeText(referralLink); toast.success(t('referral.linkCopied')); };

  const inviterBonus = referral?.inviter_bonus || 50;
  const invitedBonus = referral?.invited_bonus || 25;
  const welcomeBonus = referral?.welcome_bonus || 200;

  return (
    <DashboardLayout title={t('referral.title')}>
      <div className="max-w-3xl space-y-6">
        {/* Hero Banner */}
        <Card className="bg-gradient-to-br from-[hsl(var(--card))] to-[rgba(18,200,178,0.06)] border-[hsl(var(--border))] relative overflow-hidden">
          <div className="noise-overlay" />
          <CardContent className="p-8 relative">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[rgba(247,200,75,0.15)] flex items-center justify-center flex-shrink-0">
                <Gift className="w-7 h-7 text-[#F7C84B]" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold font-['Space_Grotesk'] mb-1">
                  {`${t('referral.inviteTitle')} $${inviterBonus}!`}
                </h2>
                <p className="text-[hsl(var(--muted-foreground))]">
                  {t('referral.inviteDesc')}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm">
              <div className="text-center">
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('referral.youEarn')}</p>
                <p className="text-2xl font-bold text-[#2DD36F] font-['Space_Grotesk'] tabular-nums">${inviterBonus}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">USDT</p>
              </div>
              <div className="text-center border-x border-white/10">
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('referral.friendExtra')}</p>
                <p className="text-2xl font-bold text-[#2DD36F] font-['Space_Grotesk'] tabular-nums">${invitedBonus}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">USDT</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('referral.friendTotal')}</p>
                <p className="text-2xl font-bold text-[hsl(var(--primary))] font-['Space_Grotesk'] tabular-nums">${welcomeBonus + invitedBonus}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">USDT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Code & Link */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('referral.codeTitle')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <code data-testid="referral-code" className="font-mono text-2xl font-bold bg-[hsl(var(--secondary))] px-6 py-3 rounded-lg tracking-wider">
                {referral?.referral_code || user?.referral_code || '-'}
              </code>
              <Button data-testid="referral-copy-button" variant="outline" className="border-[hsl(var(--border))] hover:bg-white/5" onClick={copyCode}>
                <Copy className="w-4 h-4 mr-2" /> {t('common.copy')}
              </Button>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('referral.inviteLink')}</p>
              <div className="flex items-center gap-2">
                <Input value={referralLink} readOnly className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-xs" />
                <Button variant="default" className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 flex-shrink-0" onClick={copyLink}>
                  <Share2 className="w-4 h-4 mr-2" /> {t('common.share')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="referral-stats">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-2"><Users className="w-4 h-4" /><span className="text-sm">{t('referral.invited')}</span></div>
              <p className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">{referral?.referred_count || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-2"><TrendingUp className="w-4 h-4" /><span className="text-sm">{t('referral.totalEarnings')}</span></div>
              <p className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums text-[#2DD36F]">${referral?.total_earnings?.toFixed(2) || '0.00'}</p>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-2"><Sparkles className="w-4 h-4" /><span className="text-sm">{t('referral.perInvite')}</span></div>
              <p className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">${inviterBonus}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('referral.perInviteUnit')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Referred Users */}
        {referral?.referred_users?.length > 0 && (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('referral.invitedUsersTitle')}</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--border))]">
                    <TableHead>{t('referral.userName')}</TableHead>
                    <TableHead>{t('referral.joinDate')}</TableHead>
                    <TableHead>{t('referral.bonusCol')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referral.referred_users.map((u, i) => (
                    <TableRow key={i} className="border-[hsl(var(--border))]">
                      <TableCell className="font-medium">{u.name || t('referral.defaultUser')}</TableCell>
                      <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{u.joined ? new Date(u.joined).toLocaleDateString(lang) : '-'}</TableCell>
                      <TableCell>
                        <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]">+${inviterBonus} USDT</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* How it works */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('referral.howItWorks')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-semibold text-[hsl(var(--primary))]">1</span></div>
              <div>
                <p className="font-medium">{t('referral.step1Title')}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('referral.step1Desc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-xs font-semibold text-[hsl(var(--primary))]">2</span></div>
              <div>
                <p className="font-medium">{t('referral.step2Title')}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{`${t('referral.friendBonus')} $${welcomeBonus + invitedBonus} ${t('referral.usdtBonus')}`}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[rgba(45,211,111,0.15)] flex items-center justify-center flex-shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4 text-[#2DD36F]" /></div>
              <div>
                <p className="font-medium text-[#2DD36F]">{`${t('referral.instantEarn')} $${inviterBonus} USDT!`}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('referral.step3Desc')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
